// src/pages/redress/CalculationsScreen.tsx
//
// Calculations screen for the FCA Motor Finance Redress Scheme
// (Phase 2 — M6-5). Implements Screens.docx "Calculations":
//
//   * Payment schedule table (original vs market-adjusted per year)
//   * Compensatory-interest table (auto-populated after recalculation)
//   * Three remedy summary boxes (commission repayment, APR adjustment,
//     hybrid) plus chosen remedy + lender-reported variance.
//   * 3-tier schedule source selector (actual_settlement / contractual /
//     amortisation_fallback) with tier-used confirmation pill.
//   * Nil-redress indicator when calc.nilRedress === true.
//   * Johnson remedy labelling on the commission-repayment box.
//
// Three action buttons:
//   * Save          -> PUT /calculations (draft-save the current inputs)
//   * Recalculate   -> POST /recalculate (run the formulas and persist)
//   * Reset to lender figures -> restore the saved server-side snapshot
//
// Props: agreement + agreementId (per M6-1 wiring in RedressTab).

import React, { useMemo, useState } from 'react';

import {
  Agreement,
  Calculations,
  CompensationInterestRow,
  PaymentScheduleRow,
  RecalculateInputs,
  RedressCase,
  RedressScheme,
  ScheduleSource,
  redressApi,
} from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  bannerSuccessStyle,
  buttonDangerOutline,
  buttonNeutral,
  buttonPrimary,
  buttonSecondary,
  buttonSmall,
  colors,
  fieldsetStyle,
  humaniseStatus,
  inputNarrowStyle,
  inputStyle,
  labelInlineStyle,
  labelStackStyle,
  legendStyle,
  pillDanger,
  pillSuccess,
  pillWarning,
  tableStyle,
  tdNumericStyle,
  tdStyle,
  thStyle,
} from './styles';

interface CalculationsScreenProps {
  claimId: string;
  redressCase: RedressCase;
  agreement: Agreement;
  agreementId: string;
  onCaseUpdated: (c: RedressCase) => void;
}

const DEFAULT_FIRST_YEAR = 2007;
const DEFAULT_LAST_YEAR = 2030;

// --- Editable input row ------------------------------------------------------
// The user edits the schedule as strings to keep inputs responsive (parsing
// mid-keystroke produces ugly jumps). We convert at submit time.

interface EditableScheduleRow {
  year: number;
  original: string;
  marketAdjusted: string;
}

interface EditableCommissionRow {
  year: number;
  amount: string;
}

function rowsFromSchedule(
  schedule: PaymentScheduleRow[] | undefined,
): EditableScheduleRow[] {
  if (!schedule || schedule.length === 0) {
    return [{ year: DEFAULT_FIRST_YEAR, original: '0', marketAdjusted: '0' }];
  }
  return schedule.map((r) => ({
    year: r.year,
    original: String(r.original ?? 0),
    marketAdjusted: String(r.marketAdjusted ?? 0),
  }));
}

function parseMoney(raw: string): number {
  if (raw === '' || raw === '-') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPct(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return `${n.toFixed(digits)}%`;
}

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

// --- Component ---------------------------------------------------------------

export default function CalculationsScreen({
  claimId,
  redressCase,
  agreement,
  agreementId,
  onCaseUpdated,
}: CalculationsScreenProps): React.ReactElement {
  const calc = agreement.calculations ?? {};

  // M13d: the backend persists the EFFECTIVE inputs of the last
  // recalculation on ``calculations.inputs`` — seed the editable input
  // fields from there so mounting the screen doesn't zero good data that
  // the next blind Recalculate would then persist.
  const storedInputs = calc.inputs ?? {};

  const [scheduleRows, setScheduleRows] = useState<EditableScheduleRow[]>(() =>
    rowsFromSchedule(calc.paymentSchedule),
  );
  const [commissionRows, setCommissionRows] = useState<EditableCommissionRow[]>(
    () => {
      const stored = storedInputs.commission_schedule;
      if (stored && stored.length > 0) {
        return stored.map((r) => ({
          year: r.year,
          amount: String(r.amount ?? 0),
        }));
      }
      const raw = agreement.liability?.relevantArrangement?.totalCommissionPaid;
      return [
        {
          year: DEFAULT_FIRST_YEAR,
          amount: raw !== undefined && raw !== null ? String(raw) : '0',
        },
      ];
    },
  );
  const [redressYear, setRedressYear] = useState<number>(() => {
    if (typeof storedInputs.redress_year === 'number') {
      return storedInputs.redress_year;
    }
    const rows = calc.paymentSchedule ?? [];
    if (rows.length > 0) {
      return Math.max(...rows.map((r) => r.year));
    }
    return new Date().getFullYear();
  });
  const [redressDate, setRedressDate] = useState<string>(() => {
    if (storedInputs.redress_date) {
      return storedInputs.redress_date;
    }
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [totalCostOfCredit, setTotalCostOfCredit] = useState<string>(() =>
    storedInputs.total_cost_of_credit != null
      ? String(storedInputs.total_cost_of_credit)
      : '0',
  );
  const [tccAdjusted, setTccAdjusted] = useState<string>(() =>
    storedInputs.total_cost_of_credit_adjusted != null
      ? String(storedInputs.total_cost_of_credit_adjusted)
      : '',
  );
  const [tccSimple, setTccSimple] = useState<string>(() =>
    storedInputs.total_cost_of_credit_simple != null
      ? String(storedInputs.total_cost_of_credit_simple)
      : '',
  );
  const [loanAmount, setLoanAmount] = useState<string>(() =>
    storedInputs.loan_amount != null ? String(storedInputs.loan_amount) : '0',
  );
  // Batch G: the APR the consumer paid — drives the CONRED 5.4.6R
  // nil-redress check (vs the Table 7 5th-percentile APR for the year).
  const [agreementApr, setAgreementApr] = useState<string>(() =>
    calc.agreementApr !== undefined && calc.agreementApr !== null
      ? String(calc.agreementApr)
      : storedInputs.agreement_apr != null
        ? String(storedInputs.agreement_apr)
        : '',
  );
  const [hasDca, setHasDca] = useState<boolean>(
    typeof storedInputs.has_dca === 'boolean'
      ? storedInputs.has_dca
      : Boolean(agreement.liability?.relevantArrangement?.dca),
  );
  const [hasTied, setHasTied] = useState<boolean>(
    typeof storedInputs.has_tied === 'boolean'
      ? storedInputs.has_tied
      : Boolean(agreement.liability?.relevantArrangement?.tied),
  );
  const [scheduleSource, setScheduleSource] = useState<ScheduleSource>(
    calc.scheduleSource ?? 'actual_settlement',
  );

  const [lenderReportedTotal, setLenderReportedTotal] = useState<string>(
    () => String(calc.lenderReportedTotal ?? 0),
  );

  const [saving, setSaving] = useState<boolean>(false);
  const [recalculating, setRecalculating] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const busy = saving || recalculating || resetting;

  // Totals row for the schedule
  const scheduleTotals = useMemo(() => {
    let original = 0;
    let market = 0;
    for (const r of scheduleRows) {
      original += parseMoney(r.original);
      market += parseMoney(r.marketAdjusted);
    }
    return { original, market, overcharge: original - market };
  }, [scheduleRows]);

  // --- Schedule editing -----------------------------------------------------

  const updateScheduleRow = (
    idx: number,
    patch: Partial<EditableScheduleRow>,
  ): void => {
    setScheduleRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const addScheduleRow = (): void => {
    setScheduleRows((prev) => {
      const nextYear =
        prev.length > 0
          ? Math.min(prev[prev.length - 1].year + 1, DEFAULT_LAST_YEAR)
          : DEFAULT_FIRST_YEAR;
      return [
        ...prev,
        { year: nextYear, original: '0', marketAdjusted: '0' },
      ];
    });
  };

  const removeScheduleRow = (idx: number): void => {
    setScheduleRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateCommissionRow = (
    idx: number,
    patch: Partial<EditableCommissionRow>,
  ): void => {
    setCommissionRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const addCommissionRow = (): void => {
    setCommissionRows((prev) => [
      ...prev,
      { year: DEFAULT_FIRST_YEAR, amount: '0' },
    ]);
  };

  const removeCommissionRow = (idx: number): void => {
    setCommissionRows((prev) => prev.filter((_, i) => i !== idx));
  };

  // --- Build recalc inputs --------------------------------------------------

  const buildInputs = (): RecalculateInputs => {
    const scheme: RedressScheme =
      agreement.scheme === 'scheme_1' || agreement.scheme === 'scheme_2'
        ? agreement.scheme
        : 'scheme_2';
    const finalScheme: 'scheme_1' | 'scheme_2' =
      scheme === 'scheme_1' ? 'scheme_1' : 'scheme_2';
    return {
      scheme: finalScheme,
      redress_year: redressYear,
      redress_date: redressDate || undefined,
      commission_paid: commissionRows.reduce(
        (acc, r) => acc + parseMoney(r.amount),
        0,
      ),
      commission_schedule: commissionRows.map((r) => ({
        year: r.year,
        amount: parseMoney(r.amount),
      })),
      yearly_schedule: scheduleRows.map((r) => ({
        year: r.year,
        original_payment: parseMoney(r.original),
        market_adjusted_payment: parseMoney(r.marketAdjusted),
      })),
      total_cost_of_credit: parseMoney(totalCostOfCredit),
      total_cost_of_credit_adjusted:
        tccAdjusted === '' ? null : parseMoney(tccAdjusted),
      total_cost_of_credit_simple:
        tccSimple === '' ? null : parseMoney(tccSimple),
      loan_amount: parseMoney(loanAmount),
      agreement_apr: agreementApr === '' ? null : parseMoney(agreementApr),
      has_dca: hasDca,
      has_tied: hasTied,
      schedule_source: scheduleSource,
    };
  };

  // --- Action handlers ------------------------------------------------------

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      // Save the current schedule + lender figure as a draft. We don't
      // include computed summaries -- those come from Recalculate.
      const patch: Calculations = {
        paymentSchedule: scheduleRows.map((r) => {
          const original = parseMoney(r.original);
          const market = parseMoney(r.marketAdjusted);
          return {
            year: r.year,
            original,
            marketAdjusted: market,
            diff: original - market,
          };
        }),
        lenderReportedTotal: parseMoney(lenderReportedTotal),
        scheduleSource,
      };
      const updated = await redressApi.saveCalculations(
        claimId,
        redressCase.id,
        agreementId,
        patch,
      );
      onCaseUpdated(updated);
      setMessage('Calculations saved.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to save calculations'));
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async (): Promise<void> => {
    setRecalculating(true);
    setError(null);
    setMessage(null);
    try {
      const inputs = buildInputs();
      const lender = parseMoney(lenderReportedTotal);
      const updated = await redressApi.recalculate(
        claimId,
        redressCase.id,
        agreementId,
        inputs,
        lender,
      );
      onCaseUpdated(updated);
      setMessage('Recalculation complete.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to recalculate'));
    } finally {
      setRecalculating(false);
    }
  };

  const handleReset = async (): Promise<void> => {
    // "Reset to lender figures" wipes the computed block by PUTting an
    // empty calculations object preserving only the lender-reported total.
    // M13c: the clears MUST be explicit ``null``s — ``undefined`` values
    // are dropped by JSON.stringify, so the old patch sent nothing and
    // the backend's shallow merge reset nothing.
    setResetting(true);
    setError(null);
    setMessage(null);
    try {
      const patch: Calculations = {
        paymentSchedule: [],
        compensationInterest: [],
        commissionRepayment: null,
        aprAdjustment: null,
        hybrid: null,
        chosenRemedy: null,
        chosenTotal: null,
        variance: null,
        variancePct: null,
        marketAdjustedAprPct: null,
        variationFlag: false,
        // F3 polish: the recalc also writes these display-driving flags —
        // leaving them out of the reset stranded a stale nil-redress
        // banner / Johnson pill after "Reset to lender figures".
        nilRedress: null,
        johnsonEligible: null,
        lenderReportedTotal: parseMoney(lenderReportedTotal),
      };
      const updated = await redressApi.saveCalculations(
        claimId,
        redressCase.id,
        agreementId,
        patch,
      );
      onCaseUpdated(updated);
      const updatedAgreement = updated.agreements.find(
        (a) => a.agreementId === agreementId,
      );
      setScheduleRows(
        rowsFromSchedule(updatedAgreement?.calculations?.paymentSchedule),
      );
      setMessage('Reset to lender figures.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to reset'));
    } finally {
      setResetting(false);
    }
  };

  // --- Render helpers -------------------------------------------------------

  const interestRows: CompensationInterestRow[] =
    calc.compensationInterest ?? [];
  const chosenRemedy = calc.chosenRemedy;
  const variationFlag = calc.variationFlag === true;
  // != null covers both undefined (never computed) and null (reset).
  const hasVariance = calc.variance != null;
  const isNilRedress = calc.nilRedress === true;

  return (
    <div>
      {/* ----- Section 1: Payment schedule ----- */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Payment schedule</legend>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tableStyle, minWidth: 620 }}>
            <thead>
              <tr>
                <th style={thStyle}>Year</th>
                <th style={thStyle}>Original (£)</th>
                <th style={thStyle}>Market-adjusted (£)</th>
                <th style={thStyle}>Overcharge (£)</th>
                <th style={thStyle} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {scheduleRows.map((row, idx) => {
                const original = parseMoney(row.original);
                const market = parseMoney(row.marketAdjusted);
                return (
                  <tr key={`${idx}-${row.year}`}>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        step={1}
                        min={DEFAULT_FIRST_YEAR}
                        max={DEFAULT_LAST_YEAR}
                        value={row.year}
                        onChange={(e) =>
                          updateScheduleRow(idx, {
                            year: Number(e.target.value) || DEFAULT_FIRST_YEAR,
                          })
                        }
                        style={inputNarrowStyle}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        step="0.01"
                        value={row.original}
                        onChange={(e) =>
                          updateScheduleRow(idx, { original: e.target.value })
                        }
                        style={inputStyle}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="number"
                        step="0.01"
                        value={row.marketAdjusted}
                        onChange={(e) =>
                          updateScheduleRow(idx, {
                            marketAdjusted: e.target.value,
                          })
                        }
                        style={inputStyle}
                      />
                    </td>
                    <td style={tdNumericStyle}>
                      {formatMoney(original - market)}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => removeScheduleRow(idx)}
                        style={{ ...buttonNeutral, ...buttonSmall }}
                        aria-label={`Remove row for year ${row.year}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: colors.neutral50 }}>
                <th style={{ ...thStyle, background: colors.neutral50 }}>
                  Totals
                </th>
                <td style={{ ...tdNumericStyle, fontWeight: 600 }}>
                  {formatMoney(scheduleTotals.original)}
                </td>
                <td style={{ ...tdNumericStyle, fontWeight: 600 }}>
                  {formatMoney(scheduleTotals.market)}
                </td>
                <td style={{ ...tdNumericStyle, fontWeight: 700 }}>
                  {formatMoney(scheduleTotals.overcharge)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        <button
          type="button"
          onClick={addScheduleRow}
          style={{ ...buttonSecondary, marginTop: 12 }}
        >
          + Add row
        </button>
      </fieldset>

      {/* ----- Section 1b: Commission payments ----- */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>
          Commission payments (for repayment calc)
        </legend>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tableStyle, maxWidth: 520 }}>
            <thead>
              <tr>
                <th style={thStyle}>Year paid</th>
                <th style={thStyle}>Amount (£)</th>
                <th style={thStyle} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {commissionRows.map((row, idx) => (
                <tr key={`c-${idx}`}>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      step={1}
                      min={DEFAULT_FIRST_YEAR}
                      max={DEFAULT_LAST_YEAR}
                      value={row.year}
                      onChange={(e) =>
                        updateCommissionRow(idx, {
                          year: Number(e.target.value) || DEFAULT_FIRST_YEAR,
                        })
                      }
                      style={inputNarrowStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      step="0.01"
                      value={row.amount}
                      onChange={(e) =>
                        updateCommissionRow(idx, { amount: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => removeCommissionRow(idx)}
                      style={{ ...buttonNeutral, ...buttonSmall }}
                      aria-label={`Remove commission row for year ${row.year}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={addCommissionRow}
          style={{ ...buttonSecondary, marginTop: 12 }}
        >
          + Add commission payment
        </button>
      </fieldset>

      {/* ----- Section 1c: Calculation inputs ----- */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Calculation inputs</legend>
        <div style={inputsGridStyle}>
          <label style={labelStackStyle}>
            Redress year
            <input
              type="number"
              value={redressYear}
              onChange={(e) =>
                setRedressYear(Number(e.target.value) || DEFAULT_LAST_YEAR)
              }
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            Redress date
            <input
              type="date"
              value={redressDate}
              onChange={(e) => setRedressDate(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            Loan amount (£)
            <input
              type="number"
              step="0.01"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            Total cost of credit (£)
            <input
              type="number"
              step="0.01"
              value={totalCostOfCredit}
              onChange={(e) => setTotalCostOfCredit(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            TCC adjusted cap (£, optional)
            <input
              type="number"
              step="0.01"
              value={tccAdjusted}
              onChange={(e) => setTccAdjusted(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            TCC simple cap (£, optional)
            <input
              type="number"
              step="0.01"
              value={tccSimple}
              onChange={(e) => setTccSimple(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            Agreement APR (%, nil-redress check)
            <input
              type="number"
              step="0.01"
              value={agreementApr}
              onChange={(e) => setAgreementApr(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStackStyle}>
            Schedule source
            <select
              value={scheduleSource}
              onChange={(e) => setScheduleSource(e.target.value as ScheduleSource)}
              style={inputStyle}
            >
              <option value="actual_settlement">Actual settlement (preferred)</option>
              <option value="contractual">Contractual</option>
              <option value="amortisation_fallback">Amortisation (last resort)</option>
            </select>
          </label>
        </div>
        {calc.scheduleSource && (
          <div style={{ marginTop: 8 }}>
            <span style={{ ...pillNeutralInline, fontSize: 12 }}>
              Tier used: {humaniseStatus(calc.scheduleSource)}
            </span>
          </div>
        )}
        {/* Batch G advisory output: the market-adjusted APR the remedy
            leg (D) ran with (0.83/0.79 adjustment + DCA floor). */}
        {calc.marketAdjustedAprPct != null && (
          <div style={{ marginTop: 8 }}>
            <span style={{ ...pillNeutralInline, fontSize: 12 }}>
              Market-adjusted APR (advisory):{' '}
              {formatPct(calc.marketAdjustedAprPct)}
            </span>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 12,
            flexWrap: 'wrap',
          }}
        >
          <label style={labelInlineStyle}>
            <input
              type="checkbox"
              checked={hasDca}
              onChange={(e) => setHasDca(e.target.checked)}
            />
            DCA in place
          </label>
          <label style={labelInlineStyle}>
            <input
              type="checkbox"
              checked={hasTied}
              onChange={(e) => setHasTied(e.target.checked)}
            />
            Tied arrangement
          </label>
        </div>
      </fieldset>

      {/* ----- Section 2: Compensatory interest ----- */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>
          Compensatory interest (read-only, from last recalculation)
        </legend>
        {interestRows.length === 0 ? (
          <p
            style={{
              color: colors.neutral500,
              margin: 0,
              fontSize: 13,
              fontStyle: 'italic',
            }}
          >
            Run <strong>Recalculate</strong> to populate compensatory interest.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Year</th>
                  <th style={thStyle}>Rate (%)</th>
                  <th style={thStyle}>Principal overcharge (£)</th>
                  <th style={thStyle}>Interest (£)</th>
                </tr>
              </thead>
              <tbody>
                {interestRows.map((r) => (
                  <tr key={`i-${r.year}`}>
                    <td style={tdStyle}>{r.year}</td>
                    <td style={tdNumericStyle}>
                      {/* P0-4: rate can be absent on malformed rows —
                          don't crash the tab on .toFixed of undefined. */}
                      {typeof r.rate === 'number' ? r.rate.toFixed(2) : '—'}
                    </td>
                    <td style={tdNumericStyle}>
                      {formatMoney(r.principalOvercharge)}
                    </td>
                    <td style={tdNumericStyle}>{formatMoney(r.interest)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </fieldset>

      {/* ----- Section 3: Remedy summary ----- */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Remedy summary</legend>
        <div style={remedyGrid}>
          <RemedyBox
            title="Commission repayment (Johnson remedy)"
            highlighted={chosenRemedy === 'commission_repayment'}
            rows={[
              [
                'Commission',
                formatMoney(calc.commissionRepayment?.commission),
              ],
              ['Interest', formatMoney(calc.commissionRepayment?.interest)],
              ['Total', formatMoney(calc.commissionRepayment?.total)],
            ]}
          />
          <RemedyBox
            title="APR adjustment"
            highlighted={chosenRemedy === 'apr_adjustment'}
            rows={[
              [
                'Total overcharge',
                formatMoney(calc.aprAdjustment?.totalDifferential),
              ],
              ['Interest', formatMoney(calc.aprAdjustment?.interest)],
              ['Total', formatMoney(calc.aprAdjustment?.total)],
            ]}
          />
          <RemedyBox
            title="Hybrid"
            highlighted={chosenRemedy === 'hybrid'}
            rows={[
              [
                'Commission repayment',
                formatMoney(calc.hybrid?.commissionRepaymentTotal),
              ],
              [
                'APR adjustment',
                formatMoney(calc.hybrid?.aprAdjustmentTotal),
              ],
              ['Average', formatMoney(calc.hybrid?.average)],
              [
                'Cap applied',
                calc.hybrid?.capApplied
                  ? `${calc.hybrid.capApplied} (${formatMoney(
                      calc.hybrid.capValue,
                    )})`
                  : 'None',
              ],
            ]}
          />
        </div>

        <div
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 8,
            background: chosenRemedy ? colors.primaryLight : colors.neutral50,
            border: `1px solid ${
              chosenRemedy ? colors.primaryBorder : colors.neutral200
            }`,
          }}
        >
          <div style={{ fontSize: 12, color: colors.neutral600, marginBottom: 4 }}>
            Chosen remedy
          </div>
          {isNilRedress ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span style={pillWarning}>
                Nil redress — APR below minimum threshold of cost of credit
              </span>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.neutral600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                £&nbsp;0.00
              </span>
            </div>
          ) : chosenRemedy ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: colors.neutral900 }}>
                {humaniseStatus(chosenRemedy)}
              </span>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.primary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                £&nbsp;{formatMoney(calc.chosenTotal)}
              </span>
              {calc.johnsonEligible ? (
                <span style={pillSuccess}>Johnson eligible</span>
              ) : null}
              {calc.johnsonEligible ? (
                <span style={johnsonHintStyle}>
                  Johnson threshold: ≥50% TCC and ≥22.5% loan, no caps.
                </span>
              ) : null}
            </div>
          ) : (
            <em style={{ color: colors.neutral500 }}>Not calculated yet</em>
          )}
        </div>
      </fieldset>

      {/* ----- Section 4: Lender-reported total + variance ----- */}
      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>Lender-reported total &amp; variance</legend>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1fr) minmax(280px, 2fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <label style={labelStackStyle}>
            Lender&apos;s reported total (£)
            <input
              type="number"
              step="0.01"
              value={lenderReportedTotal}
              onChange={(e) => setLenderReportedTotal(e.target.value)}
              style={inputStyle}
            />
          </label>

          <div>
            <div
              style={{
                fontSize: 12,
                color: colors.neutral600,
                marginBottom: 6,
              }}
            >
              Variance vs our calculation
            </div>
            {hasVariance ? (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    ...(variationFlag ? pillDanger : pillSuccess),
                    fontSize: 14,
                    padding: '4px 12px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  £&nbsp;{formatMoney(calc.variance)}
                  <span style={{ opacity: 0.7, marginLeft: 6 }}>
                    ({formatPct(calc.variancePct)})
                  </span>
                </span>
                {variationFlag && (
                  <span style={{ fontSize: 13, color: colors.danger }}>
                    Variation threshold breached — review before sending.
                  </span>
                )}
                {!variationFlag && calc.variance === 0 && (
                  <span style={{ fontSize: 13, color: colors.success }}>
                    Matches our calculation exactly.
                  </span>
                )}
              </div>
            ) : (
              <em
                style={{
                  color: colors.neutral500,
                  fontSize: 13,
                }}
              >
                Run <strong>Recalculate</strong> to compare against the lender
                figure.
              </em>
            )}
          </div>
        </div>
      </fieldset>

      {/* ----- Buttons ----- */}
      <div
        style={{
          marginTop: 4,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={busy}
          style={applyDisabled(buttonSecondary, busy)}
          aria-busy={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => void handleRecalculate()}
          disabled={busy}
          style={applyDisabled(buttonPrimary, busy)}
          aria-busy={recalculating}
        >
          {recalculating ? 'Recalculating…' : 'Recalculate'}
        </button>
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={busy}
          style={applyDisabled(buttonDangerOutline, busy)}
          aria-busy={resetting}
          title="Wipes the computed block but keeps the lender-reported total."
        >
          {resetting ? 'Resetting…' : 'Reset to lender figures'}
        </button>
      </div>

      {message !== null && (
        <div style={{ ...bannerSuccessStyle, marginTop: 12 }} role="status">
          {message}
        </div>
      )}
      {error !== null && (
        <div style={{ ...bannerDangerStyle, marginTop: 12 }} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---------------------------------------------------------

interface RemedyBoxProps {
  title: string;
  rows: ReadonlyArray<readonly [string, string]>;
  highlighted: boolean;
}

function RemedyBox({
  title,
  rows,
  highlighted,
}: RemedyBoxProps): React.ReactElement {
  return (
    <div style={highlighted ? remedyBoxHighlighted : remedyBoxStyle}>
      <h4
        style={{
          margin: '0 0 8px 0',
          fontSize: 14,
          fontWeight: 600,
          color: highlighted ? colors.primary : colors.neutral700,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {title}
        {highlighted && (
          <span
            style={{
              ...pillSuccess,
              fontSize: 10,
              padding: '1px 8px',
            }}
          >
            Chosen
          </span>
        )}
      </h4>
      <table style={{ width: '100%' }}>
        <tbody>
          {rows.map(([label, value], idx) => {
            const isTotal = idx === rows.length - 1;
            return (
              <tr key={label}>
                <td
                  style={{
                    padding: '3px 0',
                    color: colors.neutral600,
                    fontSize: 13,
                    fontWeight: isTotal ? 600 : 400,
                  }}
                >
                  {label}
                </td>
                <td
                  style={{
                    padding: '3px 0',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: isTotal ? 700 : 500,
                    color: colors.neutral900,
                    fontSize: isTotal ? 14 : 13,
                  }}
                >
                  {value}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- Inline styles ----------------------------------------------------------

const inputsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12,
};

const remedyGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

const remedyBoxStyle: React.CSSProperties = {
  border: `1px solid ${colors.neutral200}`,
  borderRadius: 8,
  padding: 14,
  background: colors.neutral50,
};

const remedyBoxHighlighted: React.CSSProperties = {
  ...remedyBoxStyle,
  borderColor: colors.primary,
  borderWidth: 2,
  background: colors.primaryLight,
};

// Inline pill style for the schedule-source tier-used confirmation.
const pillNeutralInline: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.6,
  background: colors.neutral100,
  color: colors.neutral700,
  border: `1px solid ${colors.neutral200}`,
};

const johnsonHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: colors.neutral500,
  fontStyle: 'italic',
};
